<template>
  <banners
    :sticky-top="true"
    :narrow="true"
  >
    <div class="m-auto w-full max-w-screen-2xl pt-6">
      <div
        v-if="loading"
        class="mx-auto flex h-80 max-w-2xl items-center justify-center py-12 sm:overflow-scroll md:overflow-hidden"
      >
        <img
          class="-top-2px loading relative h-12 w-full"
          src="@/assets/loading-v2-yellow.svg"
          alt=""
        />
      </div>

      <div
        v-if="!loading"
        class="relative mx-4 mb-12 grid grid-cols-12"
      >
        <div
          class="sticky col-span-2 hidden h-fit md:block"
          style="top: 3.5rem"
        >
          <div
            class="absolute bottom-2 left-0 z-0 rounded-full bg-light-gray"
            style="width: 2px; top: 6px"
          >
            <div
              id="activeBar"
              class="absolute z-20 rounded-full bg-dark-gray transition duration-500 ease-out"
              style="width: 2px; height: 12px"
            ></div>
          </div>
          <a
            v-for="(item, index) in tocItems"
            :id="'toc-' + item.pageId"
            :key="index"
            :ref="item.pageId"
            :data-value="item.pageId"
            :data-index="index"
            class="floating-nav-item group relative z-10 flex h-6 cursor-pointer items-center transition duration-500 ease-out"
            :class="{ active: activeSection == item.pageId }"
            @click="navChange($event)"
          >
            <div
              class="nav-item-text ml-2 inline-flex text-xs font-medium leading-tight text-body-color group-hover:text-dark-gray"
            >
              {{ item.name }}
            </div>
          </a>
        </div>

        <div
          class="col-span-12 flex flex-col-reverse gap-4 text-left md:col-span-10 md:flex-row"
        >
          <div class="w-full">
            <div
              id="eld"
              class="toc-section"
            >
              <p class="text-2xl leading-tight text-dark-blue">Equipment</p>

              <!-- alerts -->
              <div
                v-if="alertMessage"
                class="my-4 flex items-start rounded bg-red-100 p-4"
              >
                <img
                  src="@/assets/red-exclimation.svg"
                  alt="red exclamation"
                  class="-ml-2 -mt-2"
                />

                <div class="flex flex-col gap-3">
                  <p>{{ alertMessage?.title }}</p>

                  <p class="text-xs">{{ alertMessage?.text }}</p>

                  <div class="flex gap-4">
                    <button-main
                      v-if="
                        eldStatus?.eld_status === 'no_eld_vin_matches_on_coi'
                      "
                      variant="tertiary"
                      size="sm"
                      @click="handleUpdateELDAccount"
                      class="!h-10 !w-fit !p-2 !text-black"
                    >
                      Update ELD Account
                    </button-main>
                    <button-main
                      v-if="alertMessage?.submitText"
                      variant="tertiary"
                      size="sm"
                      @click="handleAlertSubmit"
                      class="!h-10 !w-fit !p-2 !text-black"
                    >
                      {{ alertMessage?.submitText }}
                    </button-main>
                  </div>
                </div>
              </div>

              <!-- warnings -->
              <div
                v-if="warningMessage"
                class="my-4 flex items-start rounded bg-orange-100 p-4"
              >
                <img
                  src="@/assets/alert-triangle.svg"
                  alt="alert triangle"
                  class="mr-2"
                />

                <div class="flex flex-col gap-2">
                  <p>{{ warningMessage?.title }}</p>

                  <p class="text-xs">{{ warningMessage?.text }}</p>

                  <button-main
                    v-if="warningMessage?.submitText"
                    variant="tertiary"
                    @click="handleAlertSubmit"
                    class="!h-10 !w-fit !p-2 !text-black"
                  >
                    {{ warningMessage?.submitText }}
                  </button-main>
                </div>
              </div>

              <div v-if="eldProvidersNotRevoked?.length === 0">
                <div class="flex flex-col items-center pt-4">
                  <img
                    class="w-72"
                    src="@/assets/eld-empty-state.svg"
                    alt="ELD Empty State"
                  />
                </div>

                <div
                  class="m-auto my-4 flex flex-col items-start gap-4 rounded-md border border-gray-over-dark p-4 text-start shadow-md md:w-[500px]"
                >
                  <p class="text-lg">Connect your ELD</p>
                  <p>
                    For the best experience, connect your ELD to verify your
                    equipment and build trust and visibility among our community
                    of brokers.
                  </p>

                  <div class="flex gap-4">
                    <button-main
                      variant="secondary"
                      @click="learnMore"
                    >
                      Learn More
                    </button-main>
                    <button-main
                      class="min-w-max"
                      variant="primary"
                      @click="handleConnectELDClick"
                    >
                      Connect an ELD
                    </button-main>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="mt-4 flex w-full flex-col gap-4"
                id="connections"
              >
                <div class="flex items-center justify-between">
                  <h2 class="text-xl leading-tight text-dark-blue">
                    ELD Connections
                  </h2>

                  <button-main
                    variant="tertiary"
                    @click="handleConnectELDClick"
                    class="ml-2 !h-10 !w-fit !p-2 !text-black"
                  >
                    New ELD Connection
                  </button-main>
                </div>

                <vue-collapsible-panel
                  v-for="(eld, index) in eldProvidersNotRevoked"
                  :key="index"
                  class="rounded-md border border-light-gray shadow-black"
                >
                  <template #title>
                    <span
                      class="inline-block flex items-center justify-between p-4 text-left text-dark-gray"
                    >
                      <span class="md: text-base text-sm">
                        {{ eld?.channel?.name }}
                      </span>
                      <p class="text-xs">
                        Connected:
                        {{ formatDate(eld?.created_at) }}
                      </p>
                    </span>
                  </template>
                  <template #content>
                    <div class="flex flex-col gap-4 bg-light-gray p-6">
                      <div class="eld-buttons flex gap-3">
                        <button-main
                          variant="tertiary"
                          @click="handleRefreshSingleEld(eld.id)"
                          class="bg-orange !h-10 !w-fit !p-2 !text-black"
                          :style="{
                            'background-color: #DADEE7;':
                              refreshButtonLoadingState.includes(eld.id),
                          }"
                        >
                          {{
                            refreshButtonLoadingState.includes(eld.id)
                              ? 'Refreshing...'
                              : 'Refresh'
                          }}
                        </button-main>
                        <button-main
                          v-if="eldProvidersNotRevoked.length > 1"
                          variant="tertiary"
                          @click="handleRevokeEld(eld.id)"
                          class="!h-10 !w-fit !p-2 !text-black"
                          :isLoading="removeButtonLoadingState.includes(eld.id)"
                        >
                          Remove
                        </button-main>
                      </div>
                      <div
                        class="flex flex-col gap-4 rounded bg-white p-4 text-start"
                      >
                        <p class="text-xl">About</p>
                        <div>
                          <p>
                            Last Updated:
                            {{ formatDateAndTime(eld?.updated_at) }}
                          </p>
                        </div>
                        <!-- <div>
                          <p>Observed Vehicles: 10</p>
                        </div> -->
                      </div>
                      <div v-if="eldCarrierVehicles?.length > 0">
                        <div
                          v-if="isMobile"
                          class="rounded bg-white"
                        >
                          <section
                            v-for="vehicle in eldCarrierVehicles"
                            :key="vehicle.id"
                            class="border-b border-gray-200 p-2"
                          >
                            <div class="flex gap-1">
                              <p>
                                {{ vehicle.vehicle?.model_year || '-' }}
                              </p>
                              <p>
                                {{ vehicle.vehicle?.make || '-' }}
                              </p>
                              <p>
                                {{ vehicle.vehicle?.model || '-' }}
                              </p>
                            </div>
                            <div class="flex items-center pt-1">
                              <p>{{ vehicle.vehicle.vin }}</p>
                              <popper
                                :show="popperStatus[vehicle.id]?.isVisible"
                                placement="top"
                                :arrow="true"
                                offsetDistance="6"
                              >
                                <div
                                  @mouseenter="togglePopper(vehicle.id, true)"
                                  @mouseleave="togglePopper(vehicle.id, false)"
                                  @click="
                                    copyItemToClipboard(
                                      vehicle.vehicle.vin,
                                      vehicle.id
                                    )
                                  "
                                  class="cursor-pointer hover:bg-light-gray"
                                  style="padding: 2px; border-radius: 3px"
                                >
                                  <div class="pl-0.5">
                                    <img
                                      class=""
                                      src="@/assets/copy-icon.svg"
                                      alt="copy-icon"
                                    />
                                  </div>
                                </div>
                                <template #content>
                                  <div
                                    class="w-auto whitespace-nowrap rounded-md bg-dark-gray px-2 py-1 text-center text-2xs text-white"
                                  >
                                    {{ popperStatus[vehicle.id]?.message }}
                                  </div>
                                </template>
                              </popper>
                            </div>
                          </section>
                        </div>
                        <table
                          v-else
                          class="striped w-full table-auto rounded bg-white"
                        >
                          <thead>
                            <tr
                              class="h-3 cursor-pointer select-none text-left text-xs text-dark-gray"
                            >
                              <th class="p-3">VIN</th>
                              <th>Year</th>
                              <th>Make</th>
                              <th>Model</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="vehicle in eldCarrierVehicles"
                              :key="vehicle.id"
                              class="h-3 cursor-pointer select-none border-t text-left text-xs text-dark-gray"
                            >
                              <td class="flex items-center p-3">
                                <p>{{ vehicle.vehicle.vin }}</p>
                                <popper
                                  :show="popperStatus[vehicle.id]?.isVisible"
                                  placement="top"
                                  :arrow="true"
                                  offsetDistance="6"
                                >
                                  <div
                                    @mouseenter="togglePopper(vehicle.id, true)"
                                    @mouseleave="
                                      togglePopper(vehicle.id, false)
                                    "
                                    @click="
                                      copyItemToClipboard(
                                        vehicle.vehicle.vin,
                                        vehicle.id
                                      )
                                    "
                                    class="cursor-pointer hover:bg-light-gray"
                                    style="padding: 2px; border-radius: 3px"
                                  >
                                    <div class="pl-0.5">
                                      <img
                                        class=""
                                        src="@/assets/copy-icon.svg"
                                        alt="copy-icon"
                                      />
                                    </div>
                                  </div>
                                  <template #content>
                                    <div
                                      class="w-auto whitespace-nowrap rounded-md bg-dark-gray px-2 py-1 text-center text-2xs text-white"
                                    >
                                      {{ popperStatus[vehicle.id]?.message }}
                                    </div>
                                  </template>
                                </popper>
                              </td>
                              <td>
                                {{ vehicle.vehicle?.model_year || '-' }}
                              </td>
                              <td>
                                {{ vehicle.vehicle?.make || '-' }}
                              </td>
                              <td>
                                {{ vehicle.vehicle?.model || '-' }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div
                        v-if="uninsuredVehicles?.length > 0"
                        class="flex flex-col gap-4 rounded bg-white p-4 text-start"
                      >
                        <p class="text-xl">Uninsured Vehicles</p>
                        <div>
                          <p class="text-body-color">
                            The following Vehicles are associated with this ELD
                            but are not found on your COI.
                          </p>
                        </div>
                        <div class="rounded-lg border border-light-gray">
                          <div
                            v-for="(vin, index) in uninsuredVehicles"
                            :key="index"
                            class="flex justify-between border-light-gray p-2"
                            :class="index !== 0 && 'border-t'"
                          >
                            <div class="flex">
                              <p>{{ vin }}</p>

                              <popper
                                :show="popperStatus[index]?.isVisible"
                                placement="top"
                                :arrow="true"
                                offsetDistance="6"
                              >
                                <div
                                  @mouseenter="togglePopper(index, true)"
                                  @mouseleave="togglePopper(index, false)"
                                  @click="copyItemToClipboard(vin, index)"
                                  class="cursor-pointer hover:bg-light-gray"
                                  style="padding: 2px; border-radius: 3px"
                                >
                                  <div class="pl-0.5">
                                    <img
                                      class=""
                                      src="@/assets/copy-icon.svg"
                                      alt="copy-icon"
                                    />
                                  </div>
                                </div>
                                <template #content>
                                  <div
                                    class="w-auto whitespace-nowrap rounded-md bg-dark-gray px-2 py-1 text-center text-2xs text-white"
                                  >
                                    {{ popperStatus[index]?.message }}
                                  </div>
                                </template>
                              </popper>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </vue-collapsible-panel>
              </div>
            </div>

            <div
              id="certificate"
              class="toc-section"
            >
              <div
                v-if="activePolicies?.length === 0"
                class="m-auto mb-4 mt-8 flex max-w-[500px] flex-col items-start gap-4 rounded-md border border-gray-over-dark p-4 shadow-md"
              >
                <p class="text-lg">Update your insurance information</p>

                <p>
                  To haul loads, submit a request for Highway to go get your
                  Certificate of Insurance (COI) from your insurance agency.
                  We’ll request it immediately.
                </p>

                <div class="flex gap-4">
                  <button-main
                    variant="secondary"
                    @click="learnMoreInsuranceExpiring"
                  >
                    Learn More
                  </button-main>
                  <button-main
                    class="min-w-max"
                    variant="primary"
                    @click="handleOpenUpdateInsurance"
                  >
                    Update Insurance
                  </button-main>
                </div>
              </div>

              <div
                v-if="activePolicies?.length > 0"
                class="mt-16"
              >
                <div
                  class="mb-4 flex flex-col justify-between md:flex-row md:items-center"
                >
                  <h2 class="text-xl leading-tight text-dark-blue">
                    Certificate of Insurance
                  </h2>
                  <div
                    class="flex w-full items-center justify-between md:w-fit md:justify-start"
                  >
                    <p class="mr-2 text-xs">
                      Received on:
                      {{ formattedActiveDate }}
                    </p>
                    <button-main
                      variant="tertiary"
                      @click="handleOpenUpdateInsurance"
                      class="!h-10 !w-fit !p-2 text-xs !text-black md:text-sm"
                    >
                      Update information
                    </button-main>
                  </div>
                </div>

                <section
                  v-if="latestInsuranceCarrierVehicles?.length > 0"
                  class="mb-6 flex flex-col gap-4 rounded bg-white text-start"
                >
                  <h2 class="text-1lg text-dark-blue">Scheduled Autos</h2>

                  <p class="text-body-color">
                    Here are the specific vehicles covered by your BIPD (or
                    "Automobile Liability") insurance policy. Vehicles absent
                    from the schedule provided on your COI are not insured.
                  </p>

                  <div class="rounded-lg border border-light-gray">
                    <table class="striped w-full table-auto">
                      <thead>
                        <tr
                          class="h-3 cursor-pointer select-none text-left text-xs text-dark-gray"
                        >
                          <th class="p-3">VIN</th>
                          <th>Year</th>
                          <th>Make</th>
                          <th>Model</th>
                          <!-- TODO -- add column with icon to show is present on ELD or not -->
                          <!-- <th>Present on ELD</th> -->
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="vehicle in latestInsuranceCarrierVehicles"
                          :key="vehicle.id"
                          class="h-3 cursor-pointer select-none border-t text-left text-xs text-dark-gray"
                        >
                          <td class="flex items-center p-3">
                            <p>{{ vehicle.vehicle.vin }}</p>
                            <popper
                              :show="popperStatus[vehicle.id]?.isVisible"
                              placement="top"
                              :arrow="true"
                              offsetDistance="6"
                            >
                              <div
                                @mouseenter="togglePopper(vehicle.id, true)"
                                @mouseleave="togglePopper(vehicle.id, false)"
                                @click="
                                  copyItemToClipboard(
                                    vehicle.vehicle.vin,
                                    vehicle.id
                                  )
                                "
                                class="cursor-pointer hover:bg-light-gray"
                                style="padding: 2px; border-radius: 3px"
                              >
                                <div class="pl-0.5">
                                  <img
                                    class=""
                                    src="@/assets/copy-icon.svg"
                                    alt="copy-icon"
                                  />
                                </div>
                              </div>
                              <template #content>
                                <div
                                  class="w-auto whitespace-nowrap rounded-md bg-dark-gray px-2 py-1 text-center text-2xs text-white"
                                >
                                  {{ popperStatus[vehicle.id]?.message }}
                                </div>
                              </template>
                            </popper>
                          </td>
                          <td>
                            {{ vehicle.vehicle?.model_year || '-' }}
                          </td>
                          <td>
                            {{ vehicle.vehicle?.make || '-' }}
                          </td>
                          <td>
                            {{ vehicle.vehicle?.model || '-' }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                <section class="flex flex-col gap-4">
                  <h2 class="text-1lg text-dark-blue">Insurance Policies</h2>

                  <p class="text-body-color">
                    Here are the insurance policies found on the latest
                    Certificate of Insurance (COI) provided to Highway by your
                    insurance agency.
                  </p>

                  <div>
                    <section
                      class="grid grid-cols-auto-fill-300 gap-3 py-4"
                      v-if="activePolicies?.length > 0"
                    >
                      <div
                        v-for="item in activePolicies"
                        :key="item.id"
                        class="w-full"
                      >
                        <insurance-card :policy="item" />
                      </div>
                    </section>

                    <section
                      class="grid grid-cols-auto-fill-300 gap-3 py-4"
                      v-else-if="
                        insurance?.fmcsa_active_and_pending_insurance.length > 0
                      "
                    >
                      <div
                        v-for="item in insurance.fmcsa_active_and_pending_insurance"
                        :key="item.id"
                        class="w-full"
                      >
                        <insurance-government-card :insurance="item" />
                      </div>
                    </section>
                  </div>
                </section>
              </div>
            </div>
          </div>

          <div
            class="mt-4 flex h-fit w-full gap-4 bg-yellow-10 p-4 text-start md:w-[400px]"
          >
            <div>
              <img
                class="mr-5 mt-1 h-6 w-6"
                src="@/assets/yellow-question-mark.svg"
                alt="question mark"
              />
            </div>
            <div class="flex flex-col gap-4">
              <p class="text-lg">Keeping trucks rolling</p>
              <p class="text-sm">
                Highway checks your ELD’s VINs and compares them to your
                Certificate of Insurance. This way, we know which of your
                vehicles are insured. If a VIN isn’t on both the ELD and the
                COI, we’ll show you so that you can correct it.
              </p>

              <button-main
                variant="tertiary"
                class="!w-fit !px-3 !py-2 !text-black"
                @click="learnMoreVehiclesInsurancePolicy"
              >
                Learn More
              </button-main>
            </div>
          </div>
        </div>
      </div>
    </div>

    <update-insurance-modal
      v-if="updateInsuranceModalVisible"
      :visible="updateInsuranceModalVisible"
      @close="handleCloseUpdateInsuranceModalVisible"
      @submit="handleUpdateInsuranceRequest"
    />

    <enter-vins-on-eld-modal
      v-if="enterVinsModalVisible"
      :visible="enterVinsModalVisible"
      @close="handleCloseEnterVinsModal"
      @submit="handleRefreshElds"
    />

    <dialog
      id="dialog"
      class="dialog-carrier-dashboard outline-none"
    >
      <carrier-portal-eld-provider-modal
        :initiallyShowConnectEldInModal="showConnectEldInModal"
      />
    </dialog>
  </banners>
</template>

<script type="text/javascript">
import dayjs from 'dayjs';
import { computed } from 'vue';
import { mapState } from 'vuex';
import _ from 'lodash';
import Popper from 'vue3-popper';
import { VueCollapsiblePanel } from '@dafcoe/vue-collapsible-panel';
import ButtonMain from '@/components/Button.vue';
import Banners from '@/components/Banners.vue';
import CarrierPortalEldProviderModal from '@/components/modals/CarrierPortalEldProviderModal.vue';
import UpdateInsuranceModal from '../../components/modals/UpdateInsuranceModal.vue';
import EnterVinsOnEldModal from '../../components/modals/EnterVinsOnELDModal.vue';
import InsuranceCard from '../../components/InsuranceCard.vue';
import InsuranceGovernmentCard from '../../components/InsuranceGovernmentCard.vue';
import { useScreenSize } from '@/composables/useScreenSize';

export default {
  name: 'ELDConnections',
  components: {
    Banners,
    ButtonMain,
    VueCollapsiblePanel,
    CarrierPortalEldProviderModal,
    UpdateInsuranceModal,
    EnterVinsOnEldModal,
    InsuranceCard,
    InsuranceGovernmentCard,
    Popper,
  },
  setup() {
    const { screenSize } = useScreenSize();

    const isMobile = computed(() => screenSize.value === 'mobile');

    return {
      isMobile,
    };
  },
  data() {
    return {
      activeSection: 'eld',
      activeSectionIndex: 0,
      scrolling: false,
      tableOfContents: [
        {
          name: 'ELD Connections',
          pageId: 'eld',
        },
        {
          name: 'Certificate of Insurance',
          pageId: 'certificate',
        },
      ],
      updateInsuranceModalVisible: false,
      enterVinsModalVisible: false,
      showConnectEldInModal: false,
      activePopper: {},
      popperStatus: {},
      showCopied: false,
      previousEldStatus: '',
      refreshButtonLoadingState: [],
      removeButtonLoadingState: [],
      alerts: {
        no_coi: {
          title: 'Your Auto policy is not on file',
          text: 'Submit a request for Highway to go get your Certificate of Insurance (COI) from your insurance agency.',
          submitText: 'Submit COI Request',
        },
        no_vins_on_coi: {
          title: 'The schedule of VINs is missing from your COI',
          text: 'The auto policy in your COI on file with Highway only covers scheduled autos, and your insurance agency failed to include the schedule of autos (VINs) on your COI. Instruct your insurance agency to send an updated Certificate of Insurance (COI) to Highway.',
          submitText: 'Submit COI Request',
        },
        missing_vins_on_eld: {
          title: 'The vehicles in your ELD account do not have VINs',
          text: 'The vehicles connected to your ELD account do not have 17-character vehicle identification numbers (VINs) present. Enter the VINs on each vehicle in your ELD account and then refresh your ELD connection.',
          submitText: 'Update ELD Account',
        },
        no_eld_vin_matches_on_coi: {
          title:
            'None of the VINs in your ELD connection are found on your COI',
          text: "Correct any mistyped VINs within your ELD account, confirm you're connected to the correct ELD account, or submit a request for Highway to go get an updated Certificate of Insurance (COI) from your insurance agency.",
          submitText: 'Submit COI Request',
        },
        no_active_vehicles_found_on_eld: {
          title: 'No recent movement observed',
          text: "Your ELD connection is considered inactive because there's been no recent activity among the vehicles found on your ELD account.",
        },
      },
      warnings: {
        some_eld_vin_matches_on_coi: {
          title:
            'Some of the vehicles on your ELD account are not found on your COI',
          text: 'If applicable, correct any mistyped VINs within your ELD account, deactivate any old vehicles within your ELD account, or instruct your insurance agency to send an updated Certificate of Insurance (COI) to Highway with all scheduled autos included.',
          submitText: 'Submit COI Request',
        },
      },
    };
  },
  computed: {
    loading() {
      return !this.$store.state.onboardingPacket.eld_integrations_have_loaded;
    },
    tocItems() {
      return this.tableOfContents;
    },
    eldProviders() {
      return this.$store.state.onboardingPacket.eld_integrations;
    },
    ...mapState({
      currentCarrierId: (state) =>
        state.users?.currentUser?.company?.carrier_id,
      insurance: (state) => state.carriers.showCarrier?.insurance,
      isCoiOnFile: (state) => state.carriers.showCarrier?.is_coi_on_file,
      insurancePolicies: (state) => state.insurance?.carrierInsurancePolicies,
    }),
    noInsuranceOnRecord() {
      return (
        this.emptyElement(this.insurance?.fmcsa_active_and_pending_insurance) &&
        this.emptyElement(this.insurance?.histories) &&
        !this.isCoiOnFile &&
        this.emptyElement(this.inactivePolicies)
      );
    },
    insurancePoliciesFiltered() {
      const sorted = _.sortBy(this.insurancePolicies, function (element) {
        var rank = {
          commercial_general_liability: 1,
          automobile_liability: 2,
          motor_truck_cargo: 3,
          workers_compensation: 4,
          trailer_interchange: 5,
        };
        return rank[element.is_type];
      });

      return sorted.sort(this.customSort);
    },
    activePolicies() {
      return this.insurancePoliciesFiltered?.filter(
        (ip) => ip.status !== 'inactive'
      );
    },
    formattedActiveDate() {
      return this.formatDate(
        this.activePolicies?.[0]?.insurance_certificate?.received_at
      );
    },
    inactivePolicies() {
      const today = new Date();
      const weekAgo = new Date(today.getTime() - 8 * 24 * 60 * 60 * 1000); // 7 days ago
      const inactivePolicies = this.insurancePoliciesFiltered?.filter(
        (record) => {
          const cancellationDate = new Date(record?.cancellation_date);
          return (
            cancellationDate >= weekAgo &&
            cancellationDate <= today &&
            record.status === 'inactive'
          );
        }
      );
      return inactivePolicies || [];
    },
    eldStatus() {
      return this.$store.state.onboardingPacket?.eld_status;
    },
    revokedElds() {
      return this.eldProviders.filter((item) => item.revoked_at !== null);
    },
    eldProvidersNotRevoked() {
      return this.eldProviders.filter((item) => item.revoked_at === null);
    },
    alertMessage() {
      const { eld_status } = this.eldStatus;

      if (this.eldProviders?.length === 0) {
        return null;
      }

      if (eld_status === 'bipd_not_on_file') {
        return this.alerts['no_coi'];
      }

      if (
        [
          'no_vins_on_coi',
          'eld_vins_not_on_coi',
          'missing_vins_on_eld',
          'no_active_vehicles_found_on_eld',
          'no_eld_vin_matches_on_coi',
        ].includes(eld_status)
      ) {
        return this.alerts[eld_status];
      }

      return null;
    },
    warningMessage() {
      const { eld_status } = this.eldStatus;

      if (eld_status === 'some_eld_vin_matches_on_coi') {
        return this.warnings[eld_status];
      }

      if (this.revokedElds?.length > 0) {
        return this.warnings['eld_disconnected'];
      }

      return null;
    },
    uninsuredVehicles() {
      return (
        this.$store.state.onboardingPacket?.eld_status?.vins_not_on_policy?.flat() ||
        []
      );
    },
    latestInsuranceCarrierVehicles() {
      return this.eldStatus?.latest_insurance_carrier_vehicles;
    },
    eldCarrierVehicles() {
      return (
        this.$store.state.onboardingPacket?.eld_carrier_vehicles
          ?.eld_carrier_vehicles || []
      );
    },
  },
  async created() {
    const carrier_id =
      this.$store.state.users?.currentUser?.company?.carrier_id;
    const company_id = this.$store.state.users?.currentUser?.company?.id;

    await Promise.all([
      this.$store.dispatch('onboardingPacket/getEldIntegrations', company_id),
      this.$store.dispatch('onboardingPacket/getLatestEldProvider', carrier_id),
      this.$store.dispatch(
        'onboardingPacket/getEldVehichleInformation',
        carrier_id
      ),
      this.$store.dispatch('insurance/getPolicyByCarrier', carrier_id),
      this.$store.dispatch('carriers/getCarriersDetails', carrier_id),
    ]);

    const res = await this.$store.dispatch('onboardingPacket/getEldStatus');
    if ([200, 201, 304].includes(res?.status) && res?.data?.eld_status) {
      this.$store.commit('onboardingPacket/setEldStatus', res?.data);
      return;
    }
  },
  mounted() {
    this.scrollListen();

    const processingStatusesArray =
      JSON.parse(localStorage.getItem('eld_connections_processing_status')) ||
      [];

    if (processingStatusesArray?.length > 0) {
      this.refreshButtonLoadingState = processingStatusesArray;

      processingStatusesArray.forEach((integration_id) => {
        this.handleRefreshSingleEld(integration_id);
      });
    }
  },
  beforeUnmount: function () {
    window.onscroll = null;
  },
  watch: {
    activeSectionIndex: function (newValue) {
      var activeBar = document.getElementById('activeBar');
      var newTop = newValue * 1.5;
      activeBar.style.transform = 'translateY(' + newTop + 'em)';
    },
  },
  methods: {
    customSort(a, b) {
      if (a?.status === 'pending_active' && b?.status !== 'pending_active') {
        return 1;
      }
      if (a?.status !== 'pending_active' && b?.status === 'pending_active') {
        return -1;
      }
      return 0;
    },
    emptyElement: function (value) {
      if (value == null) {
        return true;
      } else if (typeof value === 'object') {
        if (Object.values(value).every((x) => x === null || x === '') == true) {
          return true;
        }
        if (Array.isArray(value)) {
          if (!value.length) {
            return true;
          }
          return false;
        }
      } else {
        return false;
      }
    },
    formatDate: (date) => {
      return dayjs(date).format('MM/DD/YY');
    },
    formatDateAndTime: (date) => {
      return dayjs(date).format('MM/DD/YY, hh:mm a');
    },
    addRefreshStatusInStorage(integration_id) {
      const key = 'eld_connections_processing_status';
      let existingArray = JSON.parse(localStorage.getItem(key)) || [];
      const eldIndex = existingArray.indexOf(integration_id);

      if (eldIndex === -1) {
        existingArray.push(integration_id);

        this.refreshButtonLoadingState = existingArray;
        localStorage.setItem(key, JSON.stringify(existingArray));
      }
    },
    removeRefreshStatusInStorage(integration_id) {
      const key = 'eld_connections_processing_status';
      let existingArray = JSON.parse(localStorage.getItem(key)) || [];

      const eldIndex = existingArray.indexOf(integration_id);
      existingArray.splice(eldIndex, 1);

      this.refreshButtonLoadingState = existingArray;
      localStorage.setItem(key, JSON.stringify(existingArray));
    },
    async handleRefreshSingleEld(integration_id, show_toast = true) {
      let toast;
      if (show_toast) {
        toast = this.$toast.open({
          message: 'Refreshing connection...',
          type: 'success',
          position: 'top-right',
          duration: 5000,
        });
      }

      try {
        this.addRefreshStatusInStorage(integration_id);

        const refreshResponse = await this.$store.dispatch(
          'onboardingPacket/refreshEldProvider',
          integration_id
        );

        if ([200, 201, 204].includes(refreshResponse.status)) {
          toast?.dismiss();
          this.$toast?.open({
            type: 'info',
            message: 'Eld updated',
            position: 'top-right',
            duration: 5000,
          });

          this.removeRefreshStatusInStorage(integration_id);
          this.getEldStatus();
          await this.$store.dispatch(
            'onboardingPacket/getEldIntegrations',
            this.$store.state.users?.currentUser?.company?.id
          );
        }
      } catch (error) {
        console.error('Error refreshing ELD: ', error);
        toast?.dismiss();
        this.$toast?.open({
          type: 'error',
          message: 'An error occurred. Please try again.',
          position: 'top-right',
          duration: 5000,
        });
      }
    },
    async handleRevokeEld(integration_id) {
      console.log('Eld to revoke: ', integration_id);
      this.removeButtonLoadingState.push(integration_id);

      try {
        const removeResponse = await this.$store.dispatch(
          'onboardingPacket/removeEldProvider',
          integration_id
        );

        if ([200, 201, 204].includes(removeResponse.status)) {
          this.$toast?.open({
            type: 'info',
            message: 'Eld removed',
            position: 'top-right',
            duration: 5000,
          });

          await this.$store.dispatch(
            'onboardingPacket/getEldIntegrations',
            this.$store.state.users?.currentUser?.company?.id
          );
        }
      } catch (error) {
        console.error('Error revoking ELD provider', error);
      } finally {
        const buttonIndex =
          this.removeButtonLoadingState.indexOf(integration_id);
        this.removeButtonLoadingState.splice(buttonIndex, 1);
      }
    },

    handleOpenUpdateInsurance() {
      this.updateInsuranceModalVisible = true;
    },
    handleCloseUpdateInsuranceModalVisible() {
      this.updateInsuranceModalVisible = false;
    },
    async handleUpdateInsuranceRequest(agency) {
      const { name, email, phoneNumber } = agency;

      const payload = {
        carrier_id: this.$store.state.users?.currentUser?.company?.carrier_id,
        contact_email: email,
        contact_phone: phoneNumber,
        contact_name: name,
        // company_ids: [this.$store.state.users?.currentUser?.company?.id],
        company_ids: [1],
        is_type: 'highway',
      };

      try {
        const res = await this.$store.dispatch(
          'insurance/insuranceCertificateRequest',
          payload
        );

        if ([200, 201, 204].includes(res.status)) {
          this.$toast?.open({
            type: 'info',
            message: 'Request sent',
            position: 'top-right',
            duration: 5000,
          });
        }
      } catch (error) {
        console.error('Error sending insurance request:', error);
        this.$toast?.open({
          type: 'error',
          message: 'An error occurred. Please try again.',
          position: 'top-right',
          duration: 5000,
        });
      } finally {
        this.handleCloseUpdateInsuranceModalVisible();
      }
    },
    handleOpenEnterVinsModal() {
      this.enterVinsModalVisible = true;
    },
    handleCloseEnterVinsModal() {
      this.enterVinsModalVisible = false;
    },
    async getEldStatus() {
      const res = await this.$store.dispatch('onboardingPacket/getEldStatus');
      if ([200, 201, 304].includes(res?.status) && res?.data?.eld_status) {
        this.$store.commit('onboardingPacket/setEldStatus', res?.data);
        return;
      }
    },
    handleRefreshElds() {
      this.enterVinsModalVisible = false;

      this.eldProvidersNotRevoked.forEach((eld, index) => {
        this.handleRefreshSingleEld(eld.id, index === 0);
      });
    },
    navChange: function (event) {
      event.preventDefault;
      var self = this;
      self.scrolling = true;
      this.activeSection = event.currentTarget.getAttribute('data-value');
      this.activeSectionIndex = event.currentTarget.getAttribute('data-index');
      this.scrollToSection(this.activeSection);
    },
    scrollToSection: function (value) {
      var self = this;
      var element = document.getElementById(value);
      if (!element) {
        return;
      }
      var elTop = element.offsetTop - 10;
      this.scrollTo(elTop, function () {
        self.scrolling = false;
      });
    },
    scrollListen: function () {
      var self = this;
      var section = document.querySelectorAll('.toc-section');
      var sections = {};
      var i = 0;
      Array.prototype.forEach.call(section, function (e) {
        sections[e.id] = e.offsetTop;
      });

      window.onscroll = function () {
        if (self.loading == false && self.scrolling == false) {
          var scrollPosition =
            document.documentElement.scrollTop || document.body.scrollTop;
          scrollPosition = scrollPosition + 10;
          for (i in sections) {
            if (sections[i] <= scrollPosition) {
              var newActive = document.getElementById('toc-' + i);
              if (newActive) {
                self.activeSection = newActive.getAttribute('data-value');
                self.activeSectionIndex = newActive.getAttribute('data-index');
              }
            }
          }
        }
      };
    },
    scrollTo: function (offset, callback) {
      const fixedOffset = offset.toFixed();
      const onScroll = function () {
        if (window.pageYOffset.toFixed() === fixedOffset) {
          window.removeEventListener('scroll', onScroll);
          callback();
        }
      };

      window.addEventListener('scroll', onScroll);
      onScroll();
      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    },
    learnMore: () => {
      window.open(
        'https://service.highway.com/en/articles/10070268-your-eld-connection-to-highway',
        '_blank'
      );
      return;
    },
    learnMoreVehiclesInsurancePolicy: () => {
      window.open(
        'https://service.highway.com/en/articles/9807670-why-do-i-need-to-provide-the-list-of-vehicles-covered-by-my-insurance-policy',
        '_blank'
      );
      return;
    },
    learnMoreInsuranceExpiring: () => {
      window.open(
        'https://service.highway.com/en/articles/9807632-what-should-i-do-if-my-insurance-is-expiring-soon-or-has-otherwise-been-updated',
        '_blank'
      );
      return;
    },
    handleUpdateELDAccount() {
      this.handleOpenEnterVinsModal();
    },
    handleConnectELDClick() {
      const dialog = document.getElementById('dialog');
      dialog?.showModal();
    },
    async connectSpecificEldProviderInModal() {
      const revokedEldId = this.revokedElds?.[0]?.channel_id;

      if (revokedEldId) {
        this.showConnectEldInModal = true;
        const res = await this.$store.dispatch(
          'onboardingPacket/createCarrierEldProvider',
          revokedEldId
        );

        if (res && res?.data?.id) {
          const dialog = document.getElementById('dialog');
          dialog?.showModal();
        }
      } else {
        const dialog = document.getElementById('dialog');
        dialog?.showModal();
      }
    },
    handleAlertSubmit() {
      const { eld_status } = this.eldStatus;

      if (
        eld_status === 'bipd_not_on_file' ||
        eld_status === 'no_eld_vin_matches_on_coi'
      ) {
        this.handleOpenUpdateInsurance();
        return;
      }

      if (eld_status === 'missing_vins_on_eld') {
        this.handleOpenEnterVinsModal();
        return;
      }

      // if (
      //   this.revokedElds?.length > 0 &&
      //   eld_status !== 'some_eld_vin_matches_on_coi'
      // ) {
      //   this.connectSpecificEldProviderInModal();
      //   return;
      // }

      const email = '';
      const subject = encodeURIComponent(
        'Please send my updated COI to Highway'
      );

      const company = this.$store.state.users?.currentUser?.company?.legal_name;
      const vins = this.uninsuredVehicles.join(', ');

      const body = encodeURIComponent(
        // eslint-disable-next-line no-irregular-whitespace
        `Hi there,\n\nSome scheduled autos (VINs) are missing from the COI you provided to Highway for my company, ${company}. These are the missing VINs:\n\n• ${vins}\n\nPlease send my updated COI to insurance@certs.highway.com.\nInclude all current insurance policies on the COI, including all VINs covered by the Scheduled Auto policy, as well as any reefer breakdown or trailer interchange coverage with their limit amounts and deductibles. Please also include any General Liability policies, if applicable.\n\nAdd Highway as the Certificate Holder:\nHighway App, Inc.\n5931 Greenville Ave, Unit #5620\nDallas, TX 75206\n\nPlease reply to this email or contact Highway at coi@highway.com should you have any questions.\n\nThanks!`
      );

      const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;
      window.location.href = mailtoLink;
    },

    togglePopper(index, isVisible) {
      if (!this.popperStatus[index]) {
        this.popperStatus[index] = { isVisible: false, message: 'Copy' };
      }
      this.popperStatus[index].isVisible = isVisible;
    },

    async copyItemToClipboard(vin, index) {
      try {
        await navigator.clipboard.writeText(vin);
        this.popperStatus[index].message = 'Copied!';
        this.togglePopper(index, true);
        setTimeout(() => {
          this.togglePopper(index, false);
          this.popperStatus[index].message = 'Copy';
        }, 1500);
      } catch (err) {
        console.error('Error copying the vin on the Clipboard:', err);
      }
    },
  },
};
</script>

<style>
.floating-nav-item:not(.active) .nav-line-dark {
  opacity: 0;
}

.floating-nav-item.active .nav-line-dark {
  opacity: 1;
}

.floating-nav-item.active .nav-item-text {
  color: #313545;
  font-weight: bold;
  text-transform: uppercase;
  text-decoration: none;
}

.floating-nav-item:not(.active):hover .nav-line-dark {
  opacity: 1;
}

#button-main.loading-btn:disabled {
  background-color: #f7f7fa !important;
  border-color: #f7f7fa !important;
}

.eld-buttons button {
  min-width: 72px !important;
}
</style>
